// src/pages/ExpensePurchaseOrder/epoCompanyBranding.js
//
// Round EPO-Final — ONE centralized Owning-Company / Legal-Entity branding
// configuration for the three sister companies that can own an EPO:
//
//   SSWHITE                  S.S. White Technologies
//   SHUKLA_MEDICAL           Shukla Medical
//   SHUKLA_SURGICAL_SUPPORT  Shukla Surgical Support
//
// This is the BUYER / legal entity of the purchase order. It is completely
// separate from — and must never be confused with — the Vendor/Payee company
// stored in EpoHeader.Company (e.g. "AMAZON CAPITAL SERVICES").
//
// Consumers:
//   - epoToPdf.js — company-aware Purchase Order PDF (logo, legal name,
//     brand colors, Ship-To legal entity, contact block)
//   - (pending schema approval) the Create-EPO "Company / Legal Entity"
//     selector and the list-page legal-entity filters will share
//     EPO_COMPANY_OPTIONS + normalizeEpoCompanyCode so display labels and
//     stored codes stay consistent everywhere.
//
// Sources (no colors invented, no logos generated):
//   - Logo assets already in the repo:
//       src/assets/images/sswhite.png              (S.S. White wordmark)
//       src/assets/images/shukla_medical_logo.png  (Shukla Medical wordmark)
//       src/assets/images/sss_logo.png             (SSS mark — copied from the
//                                                   existing approved backend
//                                                   asset wwwroot/logos/sss.png)
//   - Brand colors sampled from those approved assets
//       S.S. White deep navy  ≈ #1B2B78
//       Shukla blue           ≈ #0072BC
//       SSS accent orange     ≈ #F37A1F (used ONLY as an accent, matching the
//                                        actual SSS logo — never as the base)
//   - Legal naming / address / contact lines taken from the three real
//     company invoices supplied as branding reference (all three entities
//     share 8300 Sheen Drive, St. Petersburg, FL 33709).
//
// NOTE ON STORAGE: resolveEpoCompanyBrand() reads the FUTURE owning-company
// field from the EPO header (owningCompany / legalEntity — pending the
// approved EpoHeader schema addition). Until that column exists every EPO
// resolves to the DEFAULT (S.S. White Technologies), which preserves the
// PDF's current legal identity for all historical EPOs. It deliberately
// NEVER reads header.company — that is the Vendor/Payee.

import sswhiteLogo from "../../assets/images/sswhite.png";
import shuklaMedicalLogo from "../../assets/images/shukla_medical_logo.png";
import sssMarkLogo from "../../assets/images/sss_logo.png";

// Canonical stored codes (fixed, validated values — never free text).
export const EPO_COMPANY_CODES = Object.freeze({
    SSWHITE: "SSWHITE",
    SHUKLA_MEDICAL: "SHUKLA_MEDICAL",
    SHUKLA_SURGICAL_SUPPORT: "SHUKLA_SURGICAL_SUPPORT",
});

export const DEFAULT_EPO_COMPANY_CODE = EPO_COMPANY_CODES.SSWHITE;

// Display options for the (pending) Create-EPO selector and list filters.
export const EPO_COMPANY_OPTIONS = Object.freeze([
    { code: EPO_COMPANY_CODES.SSWHITE, label: "S.S. White Technologies" },
    { code: EPO_COMPANY_CODES.SHUKLA_MEDICAL, label: "Shukla Medical" },
    { code: EPO_COMPANY_CODES.SHUKLA_SURGICAL_SUPPORT, label: "Shukla Surgical Support" },
]);

// Round EPO-Final — list-filter vocabulary. LEGACY is a FILTER-ONLY
// pseudo-code selecting pre-column rows (OwningCompany NULL); it can never
// be stored. Values are the canonical codes the server validates against
// (see backend Model/EPO/EpoOwningCompany.cs — keep the two in sync).
export const EPO_LEGACY_FILTER_CODE = "LEGACY";
export const EPO_COMPANY_FILTER_OPTIONS = Object.freeze([
    ...EPO_COMPANY_OPTIONS.map((o) => ({ value: o.code, label: o.label })),
    { value: EPO_LEGACY_FILTER_CODE, label: "Legacy / Unassigned" },
]);

// Pretty label for a stored value; unrecognized/null = Legacy / Unassigned.
export const getEpoCompanyDisplayLabel = (raw) => {
    const code = normalizeEpoCompanyCode(raw);
    const hit = code && EPO_COMPANY_OPTIONS.find((o) => o.code === code);
    return hit ? hit.label : "Legacy / Unassigned";
};

// Filter-code for a stored value (canonical code, or LEGACY for null/bad) —
// used as the column get() so select filters match on both server and
// client (all-rows mode) with the SAME vocabulary.
export const epoCompanyFilterCodeOf = (raw) =>
    normalizeEpoCompanyCode(raw) || EPO_LEGACY_FILTER_CODE;

/**
 * Normalize any reasonable representation (stored code, display name, legacy
 * spelling) to a canonical code, or null when unrecognized. Arbitrary text
 * can never create a new legal entity.
 */
export const normalizeEpoCompanyCode = (raw) => {
    if (raw === null || raw === undefined) return null;
    const v = String(raw).trim().toUpperCase().replace(/[.\s_-]+/g, "");
    if (!v) return null;
    if (v === "SSWHITE" || v === "SSWHITETECHNOLOGIES" || v === "SSWHITETECHNOLOGIESINC" || v === "SSW") {
        return EPO_COMPANY_CODES.SSWHITE;
    }
    if (v === "SHUKLAMEDICAL" || v === "SHUKLAMEDICALINC" || v === "SM") {
        return EPO_COMPANY_CODES.SHUKLA_MEDICAL;
    }
    if (v === "SHUKLASURGICALSUPPORT" || v === "SSS" || v === "SHUKLASURGICAL") {
        return EPO_COMPANY_CODES.SHUKLA_SURGICAL_SUPPORT;
    }
    return null;
};

// ---------------------------------------------------------------------------
// Brand definitions
// ---------------------------------------------------------------------------
// colors are jsPDF-friendly [r, g, b] triples.
//   primary  — the company's main brand color (header rule, table head)
//   totalBar — GRAND TOTAL bar fill
//   accent   — secondary accent (only where the real brand uses one)
// Everything else on the PO stays neutral ink/grays so the document remains
// a restrained, grayscale-printable PURCHASE ORDER (not an invoice clone).

const SSW_NAVY = [27, 43, 120];      // sampled from sswhite.png
const SHUKLA_BLUE = [0, 114, 188];   // sampled from shukla logos
const SSS_ORANGE = [243, 122, 31];   // sampled from sss_logo.png (accent only)
const SHARED_SHIPTO_ADDRESS = Object.freeze(["8300 SHEEN DRIVE", "ST. PETERSBURG, FL 33709"]);
const DARK_INK = [33, 33, 33];

const BRANDS = Object.freeze({
    [EPO_COMPANY_CODES.SSWHITE]: {
        code: EPO_COMPANY_CODES.SSWHITE,
        displayName: "S.S. White Technologies",
        legalName: "S.S. White Technologies Inc.",
        // Typeset fallback if the logo image cannot be loaded.
        headerLines: [
            { text: "S.S. WHITE", size: 15, color: SSW_NAVY, bold: true },
            { text: "Technologies Inc.", size: 11, color: SSW_NAVY, bold: true },
        ],
        shipToName: "SS WHITE TECHNOLOGIES INC.",
        shipToAddressLines: SHARED_SHIPTO_ADDRESS,
        addressLines: ["8300 Sheen Drive, St. Petersburg, FL 33709"],
        contactLines: ["Phone: (727) 626-2800", "Sales@SSWhite.net · www.SSWhite.net"],
        colors: {
            primary: SSW_NAVY,
            tableHead: SSW_NAVY,
            totalBar: SSW_NAVY,
            accent: null,
            secondaryText: DARK_INK,
        },
        logo: { src: sswhiteLogo, naturalW: 1600, naturalH: 292, height: 32 },
        logoTextLockup: null,
    },

    [EPO_COMPANY_CODES.SHUKLA_MEDICAL]: {
        code: EPO_COMPANY_CODES.SHUKLA_MEDICAL,
        displayName: "Shukla Medical",
        legalName: "Shukla Medical",
        headerLines: [
            { text: "SHUKLA MEDICAL", size: 15, color: SHUKLA_BLUE, bold: true },
            { text: "Universal Orthopedic Extraction Technologies", size: 8.5, color: DARK_INK, bold: true },
        ],
        shipToName: "SHUKLA MEDICAL",
        shipToAddressLines: SHARED_SHIPTO_ADDRESS,
        addressLines: ["8300 Sheen Drive, St. Petersburg, FL 33709"],
        contactLines: ["Phone: (727) 626-2770", "CS@ShuklaMedical.com · www.ShuklaMedical.com"],
        colors: {
            primary: SHUKLA_BLUE,
            tableHead: SHUKLA_BLUE,
            totalBar: SHUKLA_BLUE,
            accent: null,
            secondaryText: DARK_INK,   // black secondary typography per brand
        },
        logo: { src: shuklaMedicalLogo, naturalW: 1525, naturalH: 228, height: 30 },
        logoTextLockup: null,
    },

    [EPO_COMPANY_CODES.SHUKLA_SURGICAL_SUPPORT]: {
        code: EPO_COMPANY_CODES.SHUKLA_SURGICAL_SUPPORT,
        displayName: "Shukla Surgical Support",
        legalName: "Shukla Surgical Support",
        headerLines: [
            { text: "SHUKLA", size: 15, color: SHUKLA_BLUE, bold: true },
            { text: "SURGICAL SUPPORT", size: 12, color: DARK_INK, bold: true },
        ],
        shipToName: "SHUKLA SURGICAL SUPPORT",
        shipToAddressLines: SHARED_SHIPTO_ADDRESS,
        addressLines: ["8300 Sheen Drive, St. Petersburg, FL 33709"],
        contactLines: ["Phone: +1 (844) 474-8552", "pps@shuklasurgicalsupport.com"],
        colors: {
            primary: SHUKLA_BLUE,
            tableHead: SHUKLA_BLUE,
            totalBar: SHUKLA_BLUE,
            accent: SSS_ORANGE,        // orange ONLY as an accent (matches logo)
            secondaryText: DARK_INK,
        },
        // The approved SSS asset is the icon mark; the wordmark next to it is
        // typeset (blue "SHUKLA" over dark "SURGICAL SUPPORT"), mirroring the
        // company's own invoice lockup — no new logo is generated.
        logo: { src: sssMarkLogo, naturalW: 700, naturalH: 463, height: 36 },
        logoTextLockup: [
            { text: "SHUKLA", size: 14, color: SHUKLA_BLUE, bold: true },
            { text: "SURGICAL SUPPORT", size: 10.5, color: DARK_INK, bold: true },
        ],
    },
});

/**
 * Brand lookup by canonical code (unknown/blank → default S.S. White, which
 * preserves the historical PDF identity for legacy EPOs until the approved
 * owning-company backfill decision).
 */
export const getEpoCompanyBrand = (code) =>
    BRANDS[normalizeEpoCompanyCode(code) || DEFAULT_EPO_COMPANY_CODE];

/**
 * Resolve the owning-company brand for an EPO header object.
 * Reads ONLY owning-company style fields (future EpoHeader column — pending
 * schema approval). NEVER header.company / Company: that is the Vendor/Payee
 * and must not be repurposed.
 */
export const resolveEpoCompanyBrand = (header) => {
    const raw =
        header?.owningCompany ?? header?.OwningCompany ??
        header?.legalEntity ?? header?.LegalEntity ??
        null;
    return getEpoCompanyBrand(raw);
};

// ---------------------------------------------------------------------------
// Logo loading (browser-side) — bundled PNG → dataURL for jsPDF.addImage.
// Cached per source; resolves null on any failure so the PDF builder can fall
// back to the typeset header instead of ever failing PDF generation.
// ---------------------------------------------------------------------------
const _logoCache = {};

export const loadBrandLogo = (logo) => {
    if (!logo || !logo.src) return Promise.resolve(null);
    if (_logoCache[logo.src]) return Promise.resolve(_logoCache[logo.src]);
    return new Promise((resolve) => {
        try {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = img.naturalWidth || logo.naturalW;
                    canvas.height = img.naturalHeight || logo.naturalH;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0);
                    const data = {
                        dataUrl: canvas.toDataURL("image/png"),
                        w: canvas.width,
                        h: canvas.height,
                    };
                    _logoCache[logo.src] = data;
                    resolve(data);
                } catch {
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = logo.src;
        } catch {
            resolve(null);
        }
    });
};
